import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpGet, httpPost, httpPut } from "../../services/httpService";
import { logError } from "../../services/logService";
import {
  getConsentDataUrl,
  saveConsentFormUrl,
  saveRatingUrl,
  getPharmacyNameByClinicalServiceIdUrl,
  authenticatePatientUrl,
  rescheduleServiceUrl,
  cancelAppointmentUrl,
  getPharmacyServiceSettingsUrl,
  getPrescriptionUrl,
  updatePrescriptionUrl,
} from "../../apiUrls/apiUrls";

const initialState = {
  isConsentLoading: false,
  isBookingCancel: false,
  isReschedule: false,
  consentData: [],
  consentFormModel: [],
  bookingTimePeriod: "",
  healthyAdvice: [],
  nhsFormData: {
    isPatientWantsHelp: "",
    patientWeight: "",
    patientHeight: "",
    doPatientSmoke: "",
    doPatientDrink: "",
    doPatientDoExercise: "",
    bookingDate: "",
    bookingTimeSlot: "",
  },
  nhsConsentData: {
    patientId: "00000000-0000-0000-0000-000000000000",
    consent: false,
    consentSignature: "base64",
    patientName: "Dori's",
    prescriptionItemId: "00000000-0000-0000-0000-000000000000",
    PrescriptionId: "00000000-0000-0000-0000-000000000000",
    ethnicity: 1,
    contactType: 1,
    contactNumber: "03025992195",
    dateOfIntervention: "2023-11-16T12:00:00Z",
    sendSms: false,
    healthyLivingAdvice: [],
    isDeclined: false,
    createdBy: "user",
    prescriptionIncomingId: "string",
    medications: [
      {
        drugName: "name",
        dosage: "name",
        prescriptionItemId: "00000000-0000-0000-0000-000000000000",
        createdOn: "2023-11-16T12:00:00Z",
        relatedConditions: [],
      },
    ],
    isProcessedNMS: false,
    patientMedicationId: [],
    rating: "",
    isRating: false,
  },
  currentServiceId: "",
  targetPharmacyName: "",
  patientConfirmationEmail: "",
  isPatientAuthenticated: false,
  rescheduleBookingModel: [],
  selectedPatientDateTime: "",
  serviceId: "6569d675a07b6dcf7a42f2b5",
  pharmacyServiceSettings: null,
  prescriptionStatusMessage: "",
};

export const getConsentData = createAsyncThunk(
  "External/getConsentData",
  async (param) => {
    const { obj, clinicalServiceID } = param;
    const searchType = 2;
    const respData = await httpPost(
      getConsentDataUrl(searchType, clinicalServiceID),
      obj
    );
    return respData;
  }
);
export const saveConsentFormData = createAsyncThunk(
  "External/saveConsentFormData",
  async (params, thunkAPI) => {
    const { data, serviceId } = params;
    const respData = await httpPost(saveConsentFormUrl(serviceId), data);
    return respData;
  }
);
export const saveRatingData = createAsyncThunk(
  "External/saveRatingData",
  async (data, thunkAPI) => {
    const respData = await httpPost(saveRatingUrl(), data);
    return respData;
  }
);
export const getPharmacyNameByServiceId = createAsyncThunk(
  "External/getPharmacyNameByServiceId",
  async (param) => {
    const respData = await httpGet(
      getPharmacyNameByClinicalServiceIdUrl(param)
    );
    return respData;
  }
);
export const authenticatePatientData = createAsyncThunk(
  "External/authenticatePatientData",
  async (param) => {
    const { obj, clinicalServiceID } = param;
    const respData = await httpPost(
      authenticatePatientUrl(clinicalServiceID),
      obj
    );
    return respData;
  }
);
export const rescheduleServiceData = createAsyncThunk(
  "External/rescheduleServiceData",
  async (param) => {
    const respData = await httpPost(rescheduleServiceUrl(), param);
    return respData;
  }
);
export const cancelAppointmentData = createAsyncThunk(
  "External/cancelAppointmentData",
  async (clinicalServiceId) => {
    const respData = await httpPost(cancelAppointmentUrl(clinicalServiceId));
    return respData;
  }
);
export const getPharmacyServiceSettingsData = createAsyncThunk(
  "External/getPharmacyServiceSettingsData",
  async (param) => {
    const { pharmacyId, serviceID } = param;
    const respData = await httpGet(getPharmacyServiceSettingsUrl(pharmacyId, serviceID));
    return respData;
  }
);
export const getPrescriptionData = createAsyncThunk(
  "External/getPrescriptionData",
  async (param) => {
    const { obj, clinicalServiceID } = param;
    const respData = await httpPost(
      getPrescriptionUrl(clinicalServiceID),
      obj
    );
    return respData;
  }
);
export const updatePrescriptionData = createAsyncThunk(
  "External/updatePrescriptionData",
  async (param) => {
    const respData = await httpPost(
      updatePrescriptionUrl(),
      param
    );
    return respData;
  }
);

export const consentSlice = createSlice({
  name: "consent",
  initialState,
  reducers: {
    setIsConsentLoading: (state, action) => {
      state.isConsentLoading = action?.payload || false;
    },
    setIsReschedule: (state, action) => {
      state.isReschedule = action?.payload || false;
    },
    setNhsFormData: (state, action) => {
      state.nhsFormData = action?.payload || null;
    },
    setBookingDateData: (state, action) => {
      state.nhsFormData.bookingDate = action?.payload || null;
    },
    setHealthyAdviceData: (state, action) => {
      state.healthyAdvice = action?.payload || null;
    },
    setConsentFormData: (state, action) => {
      state.consentFormModel = action?.payload || null;
    },
    setBookingTimePeriodData: (state, action) => {
      state.bookingTimePeriod = action?.payload || "";
    },
    setRatingData: (state, action) => {
      state.rating = action?.payload || null;
    },
    setCurrentServiceIdData: (state, action) => {
      state.currentServiceId = action?.payload || "";
    },
    setPatientConfirmationEmailData: (state, action) => {
      state.patientConfirmationEmail = action?.payload || "";
    },
    setPatientConsents: (state, action) => {
      state.consentData = action?.payload || [];
    },
    setIsPatientAuthenticatedData: (state, action) => {
      state.isPatientAuthenticated = action?.payload || false;
    },
    setRescheduleBookingModelData: (state, action) => {
      state.rescheduleBookingModel = action?.payload || [];
    },
    setSelectedPatientDateTimeData: (state, action) => {
      state.selectedPatientDateTime = action?.payload || "";
    },
    setPrescriptionStatusMessageData: (state, action) => {
      state.prescriptionStatusMessage = action?.payload || "";
    },

    resetOrbitState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConsentData.pending, (state) => {
        state.isConsentLoading = true;
      })
      .addCase(getConsentData.fulfilled, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(getConsentData.rejected, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(saveRatingData.pending, (state) => {
        state.isConsentLoading = true;
        state.isRating = false;
      })
      .addCase(saveRatingData.fulfilled, (state, action) => {
        state.isConsentLoading = false;
        state.isRating = action.payload || false;
      })
      .addCase(saveRatingData.rejected, (state, action) => {
        state.isConsentLoading = false;
        state.isRating = false;
      })
      .addCase(getPharmacyNameByServiceId.pending, (state) => {
        state.isConsentLoading = true;
      })
      .addCase(getPharmacyNameByServiceId.fulfilled, (state, action) => {
        state.isConsentLoading = false;
        state.targetPharmacyName = action.payload || "";
      })
      .addCase(getPharmacyNameByServiceId.rejected, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(authenticatePatientData.pending, (state) => {
        state.isConsentLoading = true;
      })
      .addCase(authenticatePatientData.fulfilled, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(authenticatePatientData.rejected, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(rescheduleServiceData.pending, (state) => {
        state.isConsentLoading = true;
      })
      .addCase(rescheduleServiceData.fulfilled, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(rescheduleServiceData.rejected, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(cancelAppointmentData.pending, (state) => {
        state.isBookingCancel = true;
      })
      .addCase(cancelAppointmentData.fulfilled, (state, action) => {
        state.isBookingCancel = false;
      })
      .addCase(cancelAppointmentData.rejected, (state, action) => {
        state.isBookingCancel = false;
      })
      .addCase(getPharmacyServiceSettingsData.pending, (state) => {
        state.isBookingCancel = true;
      })
      .addCase(getPharmacyServiceSettingsData.fulfilled, (state, action) => {
        state.isBookingCancel = false;
        state.pharmacyServiceSettings = action.payload || null;
      })
      .addCase(getPharmacyServiceSettingsData.rejected, (state, action) => {
        state.isBookingCancel = false;
      })
      .addCase(getPrescriptionData.pending, (state) => {
        state.isConsentLoading = true;
      })
      .addCase(getPrescriptionData.fulfilled, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(getPrescriptionData.rejected, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(updatePrescriptionData.pending, (state) => {
        state.isConsentLoading = true;
      })
      .addCase(updatePrescriptionData.fulfilled, (state, action) => {
        state.isConsentLoading = false;
      })
      .addCase(updatePrescriptionData.rejected, (state, action) => {
        state.isConsentLoading = false;
      });
  },
});
// Action creators are generated for each case reducer function
export const {
  setNhsFormData,
  setHealthyAdviceData,
  resetOrbitState,
  setIsConsentLoading,
  setIsReschedule,
  setBookingDateData,
  setConsentFormData,
  setBookingTimePeriodData,
  setRatingData,
  setCurrentServiceIdData,
  setPatientConsents,
  setPatientConfirmationEmailData,
  setIsPatientAuthenticatedData,
  setRescheduleBookingModelData,
  setSelectedPatientDateTimeData,
  setPrescriptionStatusMessageData
} = consentSlice.actions;
export default consentSlice.reducer;
